import { Icon } from "@iconify/react";
import * as Sentry from "@sentry/react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { Button } from "@heroui/react";

import { CONSTANTS } from "@/config/constants";
import { useLogo } from "@/hooks/use-logo";

function getErrorMessage(error: unknown): string {
  if (isRouteErrorResponse(error)) {
    return `${error.status} ${error.statusText}`;
  }
  if (error instanceof Error) {
    return error.message;
  }

  return "An unexpected error occurred";
}

function getErrorStack(error: unknown): string | undefined {
  if (error instanceof Error) {
    return error.stack;
  }

  return undefined;
}

export function ErrorPage() {
  const error = useRouteError();
  const [showDetails, setShowDetails] = useState(false);
  const { full } = useLogo();

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  const errorMessage = getErrorMessage(error);
  const errorStack = getErrorStack(error);

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-secondary px-4">
      <div className="flex max-w-md flex-col items-center gap-6 text-center">
        <img alt="" height={40} src={full} />

        <motion.div
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <Icon
            className="text-muted"
            icon="solar:danger-triangle-linear"
            width={48}
          />
        </motion.div>

        <div className="flex flex-col gap-2">
          <h1 className="text-xl font-semibold text-foreground">
            Something went wrong
          </h1>
          <p className="text-sm text-muted">
            We've been notified and are looking into it. Please try refreshing
            the page.
          </p>
        </div>

        <Button
          size="md"
          variant="primary"
          onPress={() => window.location.reload()}
        >
          Refresh page
        </Button>

        {CONSTANTS.SUPPORT_EMAIL && (
          <p className="text-xs text-muted">
            If this keeps happening, contact us at{" "}
            <a
              className="text-accent hover:underline"
              href={`mailto:${CONSTANTS.SUPPORT_EMAIL}`}
            >
              {CONSTANTS.SUPPORT_EMAIL}
            </a>
          </p>
        )}

        {CONSTANTS.IS_LOCAL && (
          <div className="w-full">
            <button
              className="mb-2 text-xs text-muted hover:text-foreground transition-colors"
              onClick={() => setShowDetails((prev) => !prev)}
            >
              {showDetails ? "Hide" : "Show"} error details
            </button>
            {showDetails && (
              <div className="w-full rounded-xl bg-surface-secondary p-4 text-left">
                <p className="mb-2 text-xs font-mono text-foreground">
                  {errorMessage}
                </p>
                {errorStack && (
                  <pre className="max-h-60 overflow-auto text-[11px] font-mono text-muted whitespace-pre-wrap">
                    {errorStack}
                  </pre>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
